/**
 * Pure helpers for hand-cut vs CNC supplier cost comparison (timber frame cutting).
 */

export type HandCutInputMode = 'labor' | 'directBdft';

export interface HandCutVsCncInputs {
  boardFeet: number;
  handCutMode: HandCutInputMode;
  /** Labor path: crew × days × hours/day × $/hr */
  crewCount: number;
  cutDays: number;
  hoursPerDay: number;
  laborRatePerHour: number;
  /** Direct path: $/bdft × board feet */
  handCutDollarPerBdft: number;
  /** CNC: flat + $/bdft × board feet */
  cncDollarPerBdft: number;
  cncFlatFee: number;
  /** Shop / hand-cut add-ons ($) */
  handOverhead: number;
  handConsumables: number;
  handDieselGallons: number;
  handDieselPricePerGal: number;
  handShipping: number;
  /** CNC supplier add-ons ($) */
  cncOverhead: number;
  cncConsumables: number;
  cncDieselGallons: number;
  cncDieselPricePerGal: number;
  cncShipping: number;
}

export interface HandCutVsCncResult {
  handCutCore: number;
  handOverhead: number;
  handConsumables: number;
  handDieselCost: number;
  handShipping: number;
  handCutAddOns: number;
  handCutTotal: number;
  handCutLaborHours: number;
  handCutLaborCost: number;
  handCutImpliedDollarPerBdft: number;
  cncCore: number;
  cncVariablePortion: number;
  cncOverhead: number;
  cncConsumables: number;
  cncDieselCost: number;
  cncShipping: number;
  cncAddOns: number;
  cncTotal: number;
  deltaHandMinusCnc: number;
  /** % savings vs the more expensive path (CNC cheaper → % of hand total; hand cheaper → % of CNC total). */
  savingsPercentVsHand: number;
  cheaper: 'hand' | 'cnc' | 'tie';
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function nonNeg(n: number): number {
  return Math.max(0, Number(n) || 0);
}

export function computeHandCutVsCnc(inputs: HandCutVsCncInputs): HandCutVsCncResult {
  const bf = nonNeg(inputs.boardFeet);

  const crew = nonNeg(inputs.crewCount);
  const days = nonNeg(inputs.cutDays);
  const hpd = nonNeg(inputs.hoursPerDay);
  const laborRate = nonNeg(inputs.laborRatePerHour);
  const laborHours = crew * days * hpd;
  const laborCost = round2(laborHours * laborRate);

  const handBdftRate = nonNeg(inputs.handCutDollarPerBdft);
  const directTotal = round2(bf * handBdftRate);

  let handCutCore: number;
  let handCutLaborCostOut: number;
  if (inputs.handCutMode === 'directBdft') {
    handCutCore = directTotal;
    handCutLaborCostOut = 0;
  } else {
    handCutCore = laborCost;
    handCutLaborCostOut = laborCost;
  }

  const ho = nonNeg(inputs.handOverhead);
  const hc = nonNeg(inputs.handConsumables);
  const handDieselCost = round2(nonNeg(inputs.handDieselGallons) * nonNeg(inputs.handDieselPricePerGal));
  const hs = nonNeg(inputs.handShipping);
  const handCutAddOns = round2(ho + hc + handDieselCost + hs);
  const handCutTotal = round2(handCutCore + handCutAddOns);

  const cncRate = nonNeg(inputs.cncDollarPerBdft);
  const cncFlat = nonNeg(inputs.cncFlatFee);
  const cncVariable = round2(bf * cncRate);
  const cncCore = round2(cncFlat + cncVariable);

  const co = nonNeg(inputs.cncOverhead);
  const cc = nonNeg(inputs.cncConsumables);
  const cncDieselCost = round2(nonNeg(inputs.cncDieselGallons) * nonNeg(inputs.cncDieselPricePerGal));
  const cs = nonNeg(inputs.cncShipping);
  const cncAddOns = round2(co + cc + cncDieselCost + cs);
  const cncTotal = round2(cncCore + cncAddOns);

  const impliedHand = bf > 0 ? round2(handCutTotal / bf) : 0;
  const delta = round2(handCutTotal - cncTotal);

  let cheaper: 'hand' | 'cnc' | 'tie' = 'tie';
  if (handCutTotal < cncTotal - 0.005) cheaper = 'hand';
  else if (cncTotal < handCutTotal - 0.005) cheaper = 'cnc';

  let savingsPercentVsHand = 0;
  if (cheaper === 'cnc' && handCutTotal > 0.005) {
    savingsPercentVsHand = round2(((handCutTotal - cncTotal) / handCutTotal) * 100);
  } else if (cheaper === 'hand' && cncTotal > 0.005) {
    savingsPercentVsHand = round2(((cncTotal - handCutTotal) / cncTotal) * 100);
  }

  return {
    handCutCore,
    handOverhead: ho,
    handConsumables: hc,
    handDieselCost,
    handShipping: hs,
    handCutAddOns,
    handCutTotal,
    handCutLaborHours: round2(laborHours),
    handCutLaborCost: handCutLaborCostOut,
    handCutImpliedDollarPerBdft: impliedHand,
    cncCore,
    cncVariablePortion: cncVariable,
    cncOverhead: co,
    cncConsumables: cc,
    cncDieselCost,
    cncShipping: cs,
    cncAddOns,
    cncTotal,
    deltaHandMinusCnc: delta,
    savingsPercentVsHand,
    cheaper,
  };
}
